import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useT } from '../i18n';
import { rankForLevel } from '@rw/game-engine';
import { RANK_STYLE } from '../features/achievements/achievements';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-volt text-ink-950 hover:bg-[#c6ff63] active:bg-volt-dim shadow-[0_0_24px_rgba(182,255,59,0.25)]',
  secondary: 'bg-ink-700 text-ink-100 hover:bg-ink-600 active:bg-ink-600 border border-ink-600',
  danger: 'bg-enemy text-white hover:brightness-110 active:brightness-95',
  ghost: 'bg-transparent text-ink-200 hover:bg-ink-800 active:bg-ink-700',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  loading = false,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' | 'lg' | 'xl'; loading?: boolean }) {
  const sizes = {
    sm: 'h-9 px-3 text-sm rounded-lg',
    md: 'h-12 px-5 text-base rounded-xl',
    lg: 'h-14 px-6 text-lg rounded-2xl',
    xl: 'h-20 px-8 text-2xl rounded-3xl',
  };
  return (
    <button
      className={`inline-flex select-none items-center justify-center gap-2 font-display font-bold uppercase tracking-wide transition disabled:cursor-not-allowed disabled:opacity-40 ${VARIANTS[variant]} ${sizes[size]} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? <Spinner className="size-5" /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className = 'size-6' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-ink-700/80 bg-ink-850 p-4 ${className}`}>{children}</div>;
}

export function Stat({
  label,
  value,
  unit,
  accent,
  testId,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  accent?: 'volt' | 'gold' | 'enemy' | 'sky';
  testId?: string;
}) {
  const color = { volt: 'text-volt', gold: 'text-gold', enemy: 'text-enemy', sky: 'text-sky' }[accent ?? 'volt'];
  return (
    <div className="rounded-2xl border border-ink-700/80 bg-ink-850 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className={`font-display text-3xl font-bold tabular ${accent ? color : 'text-ink-100'}`} data-testid={testId}>
          {value}
        </span>
        {unit ? <span className="text-sm font-semibold text-ink-400">{unit}</span> : null}
      </div>
    </div>
  );
}

export function XpBar({ into, total, className = '' }: { into: number; total: number; className?: string }) {
  const pct = total > 0 ? Math.min(100, (into / total) * 100) : 0;
  return (
    <div className={`h-2.5 overflow-hidden rounded-full bg-ink-700 ${className}`} role="progressbar" aria-valuenow={into} aria-valuemax={total}>
      <div className="h-full rounded-full bg-gradient-to-r from-volt-dim to-volt transition-[width] duration-700" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** The level number on a hexagon coloured by the runner's rank tier. */
export function LevelBadge({ level, size = 'md' }: { level: number; size?: 'sm' | 'md' | 'lg' }) {
  const s = { sm: 'size-8 text-sm', md: 'size-12 text-lg', lg: 'size-16 text-2xl' }[size];
  const rank = RANK_STYLE[rankForLevel(level)];
  return (
    <div
      className={`hex-clip grid shrink-0 place-items-center font-display font-extrabold ${s}`}
      style={{ background: rank.bg, color: rank.fg }}
      aria-label={`Level ${level}`}
    >
      {level}
    </div>
  );
}

export function Avatar({ name, color, size = 'md', src }: { name: string; color: string; size?: 'sm' | 'md' | 'lg'; src?: string | null }) {
  const [broken, setBroken] = useState(false);
  const s = { sm: 'size-8 text-sm', md: 'size-11 text-lg', lg: 'size-20 text-3xl' }[size];
  const ring = { sm: 'ring-2', md: 'ring-2', lg: 'ring-4' }[size];
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className={`shrink-0 rounded-full object-cover ${ring} ${s}`}
        style={{ ['--tw-ring-color' as string]: color }}
      />
    );
  }
  return (
    <div
      className={`grid shrink-0 place-items-center rounded-full font-display font-bold text-ink-950 ${s}`}
      style={{ background: color }}
      aria-hidden="true"
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const t = useT();
  const map: Record<string, string> = {
    VALID: 'bg-volt/15 text-volt border-volt/30',
    SUSPICIOUS: 'bg-warn/15 text-warn border-warn/30',
    REJECTED: 'bg-enemy/15 text-enemy border-enemy/30',
    PENDING: 'bg-sky/15 text-sky border-sky/30',
    LOCAL: 'bg-ink-700 text-ink-200 border-ink-600',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${map[status] ?? map.LOCAL}`}>
      {t(status)}
    </span>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between">
      <h2 className="font-display text-lg font-bold uppercase tracking-wider text-ink-200">{children}</h2>
      {action}
    </div>
  );
}
